<template>
  <Modal :show="true" @close="$emit('close')" class="residentials-update">
    <StepperComponent :value="steps" class="residentials-update__stepper">
      <template #navigation="{ meta, next }">
        <ButtonComponent :label="$t('cancel')" :icon="['fal', 'times']" @click="$emit('close')" />
        <span style="margin-right: auto"></span>
        <ButtonComponent
          v-if="meta.next"
          theme="primary"
          :label="$t('next')"
          :icon="['fal', 'angle-right']"
          :disabled="!selected.key || !residentials.length"
          @click="next"
        />
        <Alert v-if="!meta.next && error" theme="error" style="max-width: 400px">
          {{ error }}
        </Alert>
        <ButtonComponent
          v-if="!meta.next && !error"
          :label="$t('save')"
          :success-label="$t('completed')"
          :loading-label="$t('saving')"
          :icon="['fal', 'arrow-to-bottom']"
          theme="primary"
          type="button"
          :promise="promise"
          @click="submit"
          @onSuccess="$emit('done')"
        />
      </template>
    </StepperComponent>
  </Modal>
</template>

<script>
import Bus from '@/bus'
import API from '@/api/Residential'
import Steps from './Steps'
import { mapState } from 'vuex'
import fields from '@/components/Residential/StepsFields/fields.js'
import defaultFields from '@/components/Residential/StepsFields/defaults.js'
import { buildStepsFields, setByPath, mergeAllDeepRight, dotNotationToObject } from '@/utils'
import { getFieldsValues, setFieldsValue } from '@/components/Residential/StepsFields/utils.js'

import { ModalComponent as Modal } from 'vue-elder-modal'
import { StepperComponent } from 'vue-elder-stepper'
import { AlertComponent as Alert } from 'vue-elder-alert'
import Card from '@/components/Card'

const Model = {}

function getDefaultData(val) {
  switch (val.key) {
    case 'facilities':
    case 'properties': {
      return {
        properties: [],
      }
    }
    case 'status': {
      return {
        status: 'draft',
      }
    }
    default: {
      return { [val.key]: null }
    }
  }
}
export default {
  watch: {
    selected: {
      handler: 'setDefaultValues',
      deep: true,
    },
  },
  props: {
    residentials: Array,
    project: { type: Object, required: true },
    disabledFields: Object,
  },
  data() {
    return {
      data: {},
      defaultData: {},
      promise: null,
      view: 'residential-update',
      selected: { value: null },
      steps: [],
    }
  },

  computed: {
    ...mapState('Project', {
      units(state) {
        return this.$path('item.metadata.limits.units', state)
      },
      addons(state) {
        return this.$path('item.metadata.addons', state) || []
      },
    }),
    saleTypes() {
      return Array.from(new Set(this.residentials.map(r => r.sale.type)))
    },
    schemas() {
      return [...Array.from(defaultFields.values() || []).map(i => i(this))].filter(i => {
        const placement = i.placement instanceof Array ? i.placement : [i.placement]

        return ['residential-update'].find(i => placement.map(p => p.split(':').shift()).includes(i))
      })
    },
    error() {
      if (!this.selected) return
      if (
        ((this.selected.key === 'status' && this.residentials.find(r => r.status === 'sold')) ||
          this.data.status === 'sold') &&
        this.project.metadata.addons.includes('project:bid')
      )
        return this.$t('residentialStatusBidLock')
      return
    },

    valueComp: {
      get() {
        return this.data
      },
      set(newVal) {
        if (this.hasMissingAddons(newVal)) return

        if (newVal.slot) {
          const { key, value } = newVal
          return setByPath(key, value, this.data)
        }

        const fieldsData = this.setFieldsValue(newVal, this.schemas)
        this.data = mergeAllDeepRight(this.data, fieldsData)
      },
    },
  },
  methods: {
    getFieldsValues,
    setFieldsValue,
    hasMissingAddons(value) {
      if (this.selected.key === 'status') {
        if (this.addons.includes('project:shop-purchase') && ['draft', 'sold'].includes(value.value)) return false
        if (!this.addons.includes('project:presentation') && !['draft', 'upcoming'].includes(value.value)) {
          Bus.emit('pricing:show', {
            addon: 'project:presentation',
            options: { units: this.units },
            action: () => this.addon('project:presentation'),
          })
            .then(() => false)
            .catch(() => this.reset())
        }
      }
      return false
    },
    setDefaultValues() {
      this.reset()
    },
    reset() {
      const value = this.selected.key ? getDefaultData(this.selected) : Model
      this.data = value
    },
    addon(addon) {
      return this.$store
        .dispatch('Project/addon', {
          addon,
          state: true,
          project: this.project.id,
        })
        .then(() => this.addons.push(addon))
    },
    getKey() {
      const key = this.selected.key.split('-')[0]

      switch (key) {
        case 'facilities':
          return 'properties'
        default:
          return key
      }
    },

    submit() {
      if (!this.data) return
      const key = this.getKey()
      const path = this.selected.parent ? `${this.selected.parent}.${this.selected.key}` : key
      const data = dotNotationToObject({
        [path]: this.data[key],
      })

      this.promise = API.mutate(
        { ids: this.residentials.map(r => r.id), data },
        {
          transformPayload: false,
          mutation: 'ResidentialsUpdate',
        },
      )
    },
  },

  created() {
    this.reset()

    let props = {
      value: () => this.valueComp,
      selected: this.selected,
      residentials: this.residentials,
      disabledFields: this.disabledFields,
      schemas: this.schemas,
      fields: buildStepsFields(Array.from(fields.values()), this.schemas, this),
    }

    let listeners = {
      updateValue: e => (this.valueComp = e),
    }

    this.steps = Steps().map(s => {
      s.props = {
        ...(s.props || {}),
        ...props,
      }
      s.listeners = {
        ...listeners,
      }
      return s
    })
  },

  components: {
    Card,
    Modal,
    StepperComponent,
    Alert,
  },
}
</script>

<style lang="scss">
.residentials-update {
  &__stepper {
    width: 100%;
    max-width: 1000px;
  }

  .elder-stepper__component {
    margin-top: 0;
  }
  .elder-stepper__breadcrumbs {
    display: none;
  }
}
</style>
